const normalize = (value: string) =>
  value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();

/**
 * Catalogue descriptions are authored with the product name as their opening
 * heading, which restated the <h1> one level down and told a crawler nothing
 * about the section. The section supplies its own descriptive label now, so a
 * leading heading that is just the product name again is dropped.
 */
export const stripDuplicateTitleHeading = (
  markdown: string,
  productName?: string,
): string => {
  if (!productName) {
    return markdown;
  }

  const [firstLine, ...rest] = markdown.trimStart().split('\n');
  const heading = firstLine?.match(/^#{1,6}\s*(.+?)\s*$/)?.[1];

  if (!heading) {
    return markdown;
  }

  const a = normalize(heading);
  const b = normalize(productName);

  if (!a || !b || !(a === b || a.includes(b) || b.includes(a))) {
    return markdown;
  }

  return rest.join('\n').trimStart();
};

type HastNode = {
  type: string;
  tagName?: string;
  properties?: Record<string, unknown>;
  children?: HastNode[];
};

const HEADING_TAG = /^h([1-6])$/;

/** The section's own "Product Description" label is the <h2>. */
export const DESCRIPTION_TOP_HEADING_LEVEL = 3;
export const DESCRIPTION_MAX_HEADING_LEVEL = 4;

/**
 * Catalogue descriptions use markdown heading levels loosely: one opens on
 * `###` and follows it with `##`, another jumps from `#` straight to `####`.
 * Mapping each level to a fixed tag let that disorder through, so the page
 * outline went h2 → h4 → h3.
 *
 * Headings are instead renumbered by their place in the outline: a heading
 * nests one level below the closest earlier heading the author made bigger,
 * starting at h3 under the section's h2 and capped at h4. Raw HTML headings
 * are included, since `rehype-raw` has already turned them into elements by
 * the time this runs. The authored level is kept on `data-source-level` so
 * the description keeps its existing look.
 */
export const rehypeNormalizeHeadings = () => (tree: HastNode) => {
  const authoredStack: number[] = [];

  const walk = (node: HastNode) => {
    const authored = Number(node.tagName?.match(HEADING_TAG)?.[1]);

    if (node.type === 'element' && authored) {
      while (
        authoredStack.length > 0 &&
        authoredStack[authoredStack.length - 1] >= authored
      ) {
        authoredStack.pop();
      }

      const level = Math.min(
        DESCRIPTION_TOP_HEADING_LEVEL + authoredStack.length,
        DESCRIPTION_MAX_HEADING_LEVEL,
      );

      authoredStack.push(authored);
      // A rehype plugin transforms the tree it is handed in place.
      Object.assign(node, {
        tagName: `h${level}`,
        properties: { ...node.properties, dataSourceLevel: authored },
      });
    }

    node.children?.forEach(walk);
  };

  walk(tree);
};
